/**
 * Cross-domain journey tracking across a small set of related, first-party
 * sites (prawkostart.pl <-> related.example), plus logging outbound clicks
 * to a domain we don't control (kopama.pl, hosted elsewhere entirely).
 *
 * Not a Lit component — a plain browser-side class, usable from EJS pages or
 * Lit components alike. Sibling to DrivingLicenseCostCalculator.ts in this
 * same directory; same "no auto-mount, caller instantiates with config and
 * calls .init()" convention.
 *
 * Usage (fail-closed: without hasConsent returning true, this is a no-op —
 * see the hasConsent option below):
 *   new TrafficJourney({
 *       relatedDomains: ['related.example'],
 *       outboundDomains: ['kopama.pl'],
 *       hasConsent: () => myConsentManager.hasAnalyticsConsent(),
 *   }).init();
 *
 * localStorage is per-origin, so prawkostart.pl's and related.example's
 * journey state are independent. Crossing between them works by appending
 * ?journeyId=<uuid> to the clicked link (only for relatedDomains, decorated
 * lazily via one delegated click listener — see onDocumentClick) and reading
 * it back out on the other side's init(). kopama.pl never receives this
 * parameter — it's an outboundDomains entry, logged only, never decorated.
 */

import { isValidUuid, type TrafficJourneyEventType } from '../traffic-journey/TrafficJourneyEvent.js';

export interface TrafficJourneyState {
    id: string;
    createdAt: number;
    lastSeenAt: number;
}

export interface TrafficJourneyOptions {
    /** Our own other domains — outbound clicks here get ?journeyId appended. */
    relatedDomains?: string[];
    /** Domains outside our control — clicks here are only logged, never decorated. */
    outboundDomains?: string[];
    /**
     * Gate for running at all. Fail-closed: defaults to () => false, so
     * TrafficJourney does nothing — no journeyId, no localStorage read or
     * write, no adopting a journeyId from the URL, no click listener, no
     * events sent, links behave completely normally — until the caller
     * passes a predicate that actually returns true. No consent-management
     * system exists yet anywhere in this codebase (checked before writing
     * this); wiring a real one up is out of scope here, but this default
     * makes sure nothing runs before that's in place.
     */
    hasConsent?: () => boolean;
    storageKey?: string;
    endpoint?: string;
    /** Journey expires after this much inactivity. Default: 30 days. */
    maxAgeMs?: number;
}

const DEFAULT_STORAGE_KEY = 'trafficJourney';
const DEFAULT_ENDPOINT = '/api/traffic-journey';
const DEFAULT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const JOURNEY_ID_PARAM = 'journeyId';

export class TrafficJourney {
    private readonly relatedDomains: Set<string>;
    private readonly outboundDomains: Set<string>;
    private readonly hasConsent: () => boolean;
    private readonly storageKey: string;
    private readonly endpoint: string;
    private readonly maxAgeMs: number;
    private state: TrafficJourneyState | null = null;
    private readonly handleClick = (event: MouseEvent): void => this.onDocumentClick(event);

    constructor(options: TrafficJourneyOptions = {}) {
        this.relatedDomains = new Set(options.relatedDomains ?? []);
        this.outboundDomains = new Set(options.outboundDomains ?? []);
        this.hasConsent = options.hasConsent ?? (() => false);
        this.storageKey = options.storageKey ?? DEFAULT_STORAGE_KEY;
        this.endpoint = options.endpoint ?? DEFAULT_ENDPOINT;
        this.maxAgeMs = options.maxAgeMs ?? DEFAULT_MAX_AGE_MS;
    }

    init(): this {
        if (!this.hasConsent()) {
            return this;
        }

        const adoptedFromUrl = this.acceptIncomingJourneyId();

        if (!adoptedFromUrl) {
            this.resumeOrStartJourney();
        }

        document.addEventListener('click', this.handleClick);

        return this;
    }

    /** Detaches the click listener. Safe to call even if never init()'d. */
    unmount(): this {
        document.removeEventListener('click', this.handleClick);
        return this;
    }

    private resumeOrStartJourney(): void {
        const stored = this.loadState();
        const now = Date.now();

        if (stored && now - stored.lastSeenAt <= this.maxAgeMs) {
            stored.lastSeenAt = now;
            this.state = stored;
            this.saveState();
            return;
        }

        this.state = { id: crypto.randomUUID(), createdAt: now, lastSeenAt: now };
        this.saveState();
        this.send('journey_start', { path: location.pathname, referrer: document.referrer || undefined });
    }

    /**
     * Reads ?journeyId from the current URL, adopts it if it's a valid UUID,
     * and always strips the param from the visible URL (valid or not) while
     * preserving every other query param and the hash. Returns whether a
     * valid id was adopted.
     */
    private acceptIncomingJourneyId(): boolean {
        const url = new URL(location.href);

        if (!url.searchParams.has(JOURNEY_ID_PARAM)) {
            return false;
        }

        const incoming = url.searchParams.get(JOURNEY_ID_PARAM);
        url.searchParams.delete(JOURNEY_ID_PARAM);
        const query = url.searchParams.toString();
        history.replaceState(null, '', url.pathname + (query ? `?${query}` : '') + url.hash);

        if (!incoming || !isValidUuid(incoming)) {
            return false;
        }

        const now = Date.now();
        this.state = { id: incoming, createdAt: now, lastSeenAt: now };
        this.saveState();
        return true;
    }

    private onDocumentClick(event: MouseEvent): void {
        if (!this.state) return;

        const element = event.target;
        if (!(element instanceof Element)) return;

        const anchor = element.closest('a[href]');
        if (!(anchor instanceof HTMLAnchorElement)) return;

        let url: URL;
        try {
            url = new URL(anchor.href, location.href);
        } catch {
            return;
        }

        if (url.hostname === location.hostname) return;

        if (this.relatedDomains.has(url.hostname)) {
            url.searchParams.set(JOURNEY_ID_PARAM, this.state.id);
            anchor.href = url.toString();
            this.send('cross_site', { target: url.hostname });
            return;
        }

        if (this.outboundDomains.has(url.hostname)) {
            this.send('outbound', { target: url.hostname });
        }
    }

    private send(
        type: TrafficJourneyEventType,
        extra: { path?: string; target?: string; referrer?: string } = {},
    ): void {
        if (!this.state) return;

        const payload = JSON.stringify({ journeyId: this.state.id, type, ...extra });

        // Must never block navigation or surface an error to the user —
        // analytics fails silently, always.
        try {
            const sent =
                typeof navigator.sendBeacon === 'function' &&
                navigator.sendBeacon(this.endpoint, new Blob([payload], { type: 'application/json' }));

            if (!sent) {
                void fetch(this.endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: payload,
                    keepalive: true,
                }).catch(() => {});
            }
        } catch {
            // Ignored — see comment above.
        }
    }

    private loadState(): TrafficJourneyState | null {
        try {
            const raw = localStorage.getItem(this.storageKey);
            if (!raw) return null;

            const parsed = JSON.parse(raw) as Partial<TrafficJourneyState>;
            if (
                !isValidUuid(parsed.id) ||
                typeof parsed.createdAt !== 'number' ||
                typeof parsed.lastSeenAt !== 'number'
            ) {
                return null;
            }

            return parsed as TrafficJourneyState;
        } catch {
            return null;
        }
    }

    private saveState(): void {
        if (!this.state) return;

        try {
            localStorage.setItem(this.storageKey, JSON.stringify(this.state));
        } catch {
            // Private browsing / storage disabled — journey just won't persist across reloads.
        }
    }
}
